/**
 * Campaigns.jsx
 * Every campaign, and the form that starts a new one.
 *
 * A campaign is a week of posts planned together so they say different things
 * in a sensible order — the alternative being seven separate sittings at the
 * Create screen, each with no memory of the last.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import CampaignForm from '../components/CampaignForm.jsx';
import NoBrands from '../components/NoBrands.jsx';
import { Calendar as CalendarIcon, Check, Plus, Warning } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

const STATUS_LABEL = {
  draft: 'Drafted',
  active: 'Scheduled',
  partial: 'Partly scheduled',
  done: 'Finished',
};

const BADGE = { active: 'badge--ok', partial: 'badge--warn', draft: 'badge--accent' };

export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [available, setAvailable] = useState({});
  const [planning, setPlanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ campaigns: list }, { brands: bs }] = await Promise.all([
        api.campaigns.list(),
        api.brands.list(),
      ]);
      setCampaigns(list);
      setBrands(bs);

      // Which platforms each brand can actually reach, so the form can grey out
      // the rest rather than letting you plan a week that cannot be approved.
      const reach = {};
      for (const b of bs) {
        const { brand } = await api.brands.get(b.id);
        const accounts = brand.accounts ?? [];
        reach[b.id] = {
          facebook: accounts.some((a) => a.platform === 'facebook' && a.status === 'connected'),
          instagram: accounts.some((a) => a.platform === 'instagram' && a.status === 'connected'),
        };
      }
      setAvailable(reach);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(campaign) {
    if (
      !confirm(
        `Delete "${campaign.name}"? Its drafts and anything still scheduled go with it. Posts already published are kept.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.campaigns.remove(campaign.id);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="mono">Loading…</p>;

  if (brands.length === 0) {
    return <NoBrands error={error} what="A campaign is a week of posts for one brand." />;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Campaigns</div>
          <h1>A week at a time</h1>
          <p>Plan several posts together, edit what you want, then approve the lot in one go.</p>
        </div>
        <div className="page-head__actions">
          {!planning && (
            <button className="btn btn--primary" onClick={() => setPlanning(true)}>
              <Plus /> New campaign
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {planning && (
        <CampaignForm
          brands={brands}
          available={available}
          onCancel={() => setPlanning(false)}
          onPlanned={(result) =>
            navigate(`/campaigns/${result.campaign.id}`, {
              state: { rejectedMedia: result.rejectedMedia, costPence: result.costPence },
            })
          }
        />
      )}

      {campaigns.length === 0 && !planning ? (
        <div className="card">
          <div className="empty">
            <div className="empty__art">
              <CalendarIcon size={30} />
            </div>
            <h3>No campaigns yet</h3>
            <p>
              Describe a week in a sentence or two and it comes back as drafts you can edit — with
              dates, times and images already attached.
            </p>
            <button className="btn btn--primary" onClick={() => setPlanning(true)}>
              <Plus /> Plan a week
            </button>
          </div>
        </div>
      ) : (
        campaigns.length > 0 && (
          <section className="card">
            <div className="rows">
              {campaigns.map((c) => (
                <div className="row" key={c.id}>
                  <span className="tile tile--sm">
                    <CalendarIcon size={17} />
                  </span>

                  <div style={{ minWidth: 0 }}>
                    <div className="row__label">{c.name}</div>
                    <div className="row__sub">
                      {c.brand_name} · {c.platforms.map(platformLabel).join(' + ')} ·{' '}
                      {dateRange(c.start_date, c.end_date)}
                    </div>
                    <div className="row__sub">
                      {countLine(c)}
                      {c.approved_at ? ` · approved ${formatLondon(c.approved_at)}` : ''}
                    </div>
                  </div>

                  <div className="row__end">
                    <span className={`badge ${BADGE[c.status] ?? ''}`}>
                      {c.status === 'active' && <Check size={13} />}
                      {STATUS_LABEL[c.status] ?? c.status}
                    </span>
                    <button
                      className="btn btn--sm btn--primary"
                      onClick={() => navigate(`/campaigns/${c.id}`)}
                    >
                      Open
                    </button>
                    <button
                      className="btn btn--sm btn--danger"
                      onClick={() => remove(c)}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )
      )}
    </>
  );
}

const platformLabel = (p) => (p === 'facebook' ? 'Facebook' : p === 'instagram' ? 'Instagram' : p);

const day = (d) =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${d}T12:00:00Z`),
  );

/** "14 to 18 Sep", or a single date when the campaign is one day. */
function dateRange(start, end) {
  if (!start) return '';
  if (!end || start === end) return day(start);
  // Same month reads better without repeating it: "14 to 18 Sep".
  const [, sm] = start.split('-');
  const [, em] = end.split('-');
  return sm === em ? `${start.split('-')[2].replace(/^0/, '')} to ${day(end)}` : `${day(start)} to ${day(end)}`;
}

/** "7 posts · 5 scheduled, 2 drafted" — where the week has actually got to. */
function countLine(c) {
  const parts = [];
  if (c.published_count) parts.push(`${c.published_count} published`);
  if (c.scheduled_count) parts.push(`${c.scheduled_count} scheduled`);
  if (c.draft_count) parts.push(`${c.draft_count} drafted`);
  if (c.failed_count) parts.push(`${c.failed_count} failed`);
  return `${c.post_count} post${c.post_count === 1 ? '' : 's'}${parts.length ? ` · ${parts.join(', ')}` : ''}`;
}
